import { useState } from "react";
import { Clear, Search } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputBase,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { AccessHistoryRow } from "./useAccessHistoryData";

interface AccessHistorySearchBarProps {
  value: string;
  onChange: (value: string) => void;
  /** Quantidade de resultados após filtro (opcional) */
  resultCount?: number;
}

/** Campo de busca do modal "Ver tudo" — nome, CPF ou organização. */
export function AccessHistorySearchBar({
  value,
  onChange,
  resultCount,
}: AccessHistorySearchBarProps) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const [focused, setFocused] = useState(false);
  const primary = theme.palette.primary.main;
  const hasValue = value.trim().length > 0;

  const placeholder = isMobileLayout
    ? "Nome, CPF ou organização"
    : "Buscar por nome, CPF ou organização";

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
          minHeight: 48,
          px: 1.15,
          py: 0.65,
          borderRadius: 3,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: focused
            ? alpha(primary, 0.45)
            : alpha(theme.palette.divider, 0.95),
          boxShadow: focused
            ? `0 0 0 3px ${alpha(primary, 0.12)}, 0 4px 14px rgba(15, 23, 42, 0.06)`
            : "0 2px 10px rgba(15, 23, 42, 0.04)",
          transition:
            "border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 34,
            height: 34,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: focused
              ? alpha(primary, 0.14)
              : alpha(primary, 0.08),
            color: focused ? "primary.main" : "text.secondary",
            transition: "background-color 0.2s ease, color 0.2s ease",
          }}
        >
          <Search sx={{ fontSize: 19 }} />
        </Box>

        <InputBase
          fullWidth
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          inputProps={{
            "aria-label": "Buscar por nome, CPF ou organização",
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: isMobileLayout ? "0.8125rem" : "0.875rem",
            fontWeight: 500,
            color: "text.primary",
            "& .MuiInputBase-input": {
              py: 0.35,
              px: 0,
              "&::placeholder": {
                color: "text.secondary",
                opacity: 0.72,
                fontWeight: 500,
              },
            },
          }}
        />

        {hasValue ? (
          <IconButton
            size="small"
            aria-label="Limpar busca"
            onClick={() => onChange("")}
            sx={{
              flexShrink: 0,
              width: 30,
              height: 30,
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              color: "text.secondary",
              "&:hover": {
                bgcolor: alpha(theme.palette.grey[500], 0.18),
                color: "text.primary",
              },
            }}
          >
            <Clear sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mt: 0.85,
          px: 0.25,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {["Nome", "CPF", "Organização"].map((hint) => (
            <Box
              key={hint}
              sx={{
                px: 0.85,
                py: 0.2,
                borderRadius: 99,
                bgcolor: alpha(primary, 0.06),
                border: "1px solid",
                borderColor: alpha(primary, 0.1),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  color: "text.secondary",
                  letterSpacing: "0.01em",
                }}
              >
                {hint}
              </Typography>
            </Box>
          ))}
        </Box>

        {hasValue && resultCount != null ? (
          <Typography
            variant="caption"
            sx={{
              flexShrink: 0,
              fontWeight: 700,
              fontSize: "0.6875rem",
              color: resultCount > 0 ? "primary.main" : "text.secondary",
            }}
          >
            {resultCount > 0
              ? `${resultCount} resultado${resultCount !== 1 ? "s" : ""}`
              : "Nenhum resultado"}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/** Filtra linhas por nome, CPF (identifier) ou organização. */
export function filterAccessHistoryBySearch(
  rows: AccessHistoryRow[],
  query: string
): AccessHistoryRow[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return rows;

  const queryDigits = normalizedQuery.replace(/\D/g, "");

  return rows.filter((row) => {
    const name = String(row.name ?? "").toLowerCase();
    const organization = String(row.organization ?? "").toLowerCase();
    const identifier = String(row.identifier ?? "").toLowerCase();
    const identifierDigits = String(row.identifier ?? "").replace(/\D/g, "");

    if (name.includes(normalizedQuery)) return true;
    if (organization.includes(normalizedQuery)) return true;
    if (identifier.includes(normalizedQuery)) return true;
    if (queryDigits.length >= 2 && identifierDigits.includes(queryDigits)) {
      return true;
    }

    return false;
  });
}
